'use client';

import React, { useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

// Add this global declaration for Umami
declare global {
  interface Window {
    umami?: {
      track: (eventName: string, eventData?: Record<string, any>) => void;
    };
  }
}

// Set by the pre-paint script in app/layout.tsx the moment a wrapper's CSS failsafe
// reveal (`aos-failsafe-reveal`, --motion-failsafe after first style) begins. Reading an
// attribute never forces style or layout; the previous computed-opacity check did, and on
// slow machines dozens of wrappers intersect at once right after hydration (PSI: 104ms).
const REVEALED_ATTR = 'data-aos-revealed';

interface AnimateOnScrollProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  animationClassName: string;
  threshold?: number;
  umamiEventName?: string;
  /**
   * Eager wrappers are for above-the-fold content: the animation class is
   * applied in the server HTML (pure CSS animation, fill-mode both), so the
   * content paints without waiting for hydration and is never hidden when
   * JS is absent. The IntersectionObserver is kept solely for Umami events.
   */
  eager?: boolean;
}

export const AnimateOnScroll: React.FC<AnimateOnScrollProps> = ({
  children,
  className,
  animationClassName,
  threshold = 0.1,
  umamiEventName,
  eager = false,
  style,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [settled, setSettled] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // If the CSS failsafe already began revealing this wrapper (hydration or
          // IO arrived late), don't replay the entrance from opacity 0 (F1).
          if (ref.current?.hasAttribute(REVEALED_ATTR)) {
            setSettled(true);
          }
          setIsVisible(true);

          if (umamiEventName && window.umami) {
            window.umami.track(umamiEventName);
          }

          if (ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      { threshold }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, umamiEventName]);

  return (
    <div
      ref={ref}
      className={cn(
        eager
          ? animationClassName
          : isVisible
            ? cn(animationClassName, settled && 'aos-settled')
            : 'aos-pending',
        className
      )}
      style={style}
      {...props}
    >
      {children}
    </div>
  );
};
